import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { EnvelopeSimple, Phone as PhoneIcon, LinkedinLogo, Star, User as UserIcon, UsersThree, Sparkle, ArrowClockwise } from "@phosphor-icons/react";

/**
 * ContactHistoryDrawer — Iter 85.2 · Phase 3
 *
 * Renders every Note and every Task on the client that references this
 * Contact via contact_ids. Backed by GET /clients/{cid}/contacts/{id}/history.
 * Read-only in this pass; edits still go through the Contacts tab and the
 * Note/Task surfaces.
 */
function fmtRelative(iso) {
  if (!iso) return "never";
  try {
    const dt = new Date(iso);
    const days = Math.floor((Date.now() - dt.getTime()) / (24 * 3600 * 1000));
    if (days < 0) return "future";
    if (days === 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.floor(days / 30)} mo ago`;
    return `${Math.floor(days / 365)} yr ago`;
  } catch { return ""; }
}

export default function ContactHistoryDrawer({ clientId, contactId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Iter 85.7 · Introduction Path — mutual-connection suggestions.
  const [intros, setIntros] = useState(null);
  // Iter 85.9 · Relationship Summary — the current "who they are / how
  // it's going / what's next" read. Regenerated on partner demand.
  const [summarizing, setSummarizing] = useState(false);

  useEffect(() => {
    if (!open || !clientId || !contactId) return;
    setLoading(true);
    api.get(`/clients/${clientId}/contacts/${contactId}/history`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // Load introduction candidates in parallel — non-blocking.
    api.get(`/contacts/${clientId}/${contactId}/introductions`)
      .then((r) => setIntros(r.data))
      .catch(() => setIntros(null));
  }, [open, clientId, contactId]);

  const c = data?.contact;
  const summary = c?.relationship_summary;

  const regenerateSummary = async () => {
    if (summarizing) return;
    setSummarizing(true);
    try {
      const { data: res } = await api.post(`/contacts/${clientId}/${contactId}/relationship-summary`);
      setData((d) => (d ? { ...d, contact: { ...(d.contact || {}), relationship_summary: res.relationship_summary } } : d));
      toast.success("Relationship read refreshed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Summary generation failed");
    } finally {
      setSummarizing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none bg-card max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0"
        data-testid="contact-history-drawer"
      >
        <DialogHeader className="border-b border-border px-6 pt-6 pb-4 shrink-0">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 border border-border bg-secondary/40 flex items-center justify-center shrink-0">
              <UserIcon size={22} className="text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="font-display text-xl font-bold flex items-center gap-2">
                <span className="truncate">{c?.name || "Loading…"}</span>
                {c?.is_primary && (
                  <span className="inline-flex items-center gap-1 text-[0.625rem] font-mono uppercase tracking-widest text-primary border border-primary/40 px-1.5 py-0.5">
                    <Star size={9} weight="fill" /> Primary
                  </span>
                )}
              </DialogTitle>
              {c?.title ? (
                <DialogDescription className="text-xs text-muted-foreground mt-1">
                  {c.title}
                </DialogDescription>
              ) : (
                <DialogDescription className="sr-only">Contact relationship history</DialogDescription>
              )}
              <div className="mt-2 flex items-center gap-4 flex-wrap text-xs text-muted-foreground">
                {c?.email && (
                  <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-primary">
                    <EnvelopeSimple size={11} /> {c.email}
                  </a>
                )}
                {c?.phone && (
                  <a href={`tel:${c.phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-1 hover:text-primary">
                    <PhoneIcon size={11} /> {c.phone}
                  </a>
                )}
                {c?.linkedin_url && (
                  <a
                    href={c.linkedin_url.startsWith("http") ? c.linkedin_url : `https://${c.linkedin_url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 hover:text-primary"
                  >
                    <LinkedinLogo size={11} /> LinkedIn
                  </a>
                )}
              </div>
              <div className="mt-2 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground" data-testid="contact-history-last-contacted">
                Last contacted · {fmtRelative(c?.last_contacted_at)}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 space-y-6" data-testid="contact-history-body">
          {loading && (
            <p className="text-xs text-muted-foreground">Loading history…</p>
          )}
          {!loading && data && (
            <>
              {/* Iter 85.9 · Relationship Summary — the current one-line
                  read on this contact. Sits above the raw history so a
                  partner can glance and know where they are before
                  scrolling. Regenerates on demand only. */}
              <section data-testid="contact-relationship-summary" className="border border-primary/20 bg-primary/[0.03] p-4">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary flex items-center gap-1.5">
                    <Sparkle size={11} weight="fill" /> Current relationship read
                  </div>
                  <button
                    type="button"
                    data-testid="contact-relationship-summary-refresh"
                    onClick={regenerateSummary}
                    disabled={summarizing}
                    className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary disabled:opacity-40 inline-flex items-center gap-1"
                    title={summary ? "Rebuild from every note and task referencing this contact" : "Generate the first read"}
                  >
                    <ArrowClockwise size={10} weight={summarizing ? "bold" : "regular"} className={summarizing ? "animate-spin" : ""} />
                    {summarizing ? "Reading…" : summary ? "Refresh" : "Generate"}
                  </button>
                </div>
                {summary?.text ? (
                  <>
                    <p
                      className="font-serif text-sm leading-relaxed text-foreground"
                      data-testid="contact-relationship-summary-text"
                    >
                      {summary.text}
                    </p>
                    <div
                      className="mt-2 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground/80"
                      data-testid="contact-relationship-summary-meta"
                    >
                      Read {fmtRelative(summary.generated_at)}
                      {summary.generated_by ? ` · by ${summary.generated_by}` : ""}
                      {summary.source_counts ? ` · ${summary.source_counts.notes || 0} note${(summary.source_counts.notes || 0) === 1 ? "" : "s"}, ${summary.source_counts.tasks || 0} task${(summary.source_counts.tasks || 0) === 1 ? "" : "s"}` : ""}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground italic" data-testid="contact-relationship-summary-empty">
                    No relationship read yet. Generate one to roll every note and task on this contact into a three-sentence briefing.
                  </p>
                )}
              </section>

              <section data-testid="contact-history-notes">
                <h3 className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-2">
                  Notes & meetings ({data.counts?.notes ?? 0})
                </h3>
                {(data.notes || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">Not referenced on any note yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.notes.map((n) => (
                      <li key={n.id} className="border border-border/60 bg-card p-3" data-testid={`contact-history-note-${n.id}`}>
                        <Link
                          to={n.engagement_id
                            ? `/engagements/${n.engagement_id}?tab=notes&openNote=${n.id}`
                            : `/clients/${clientId}?tab=notes&openNote=${n.id}`}
                          className="block hover:text-primary"
                        >
                          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                            {n.note_type} · {(n.meeting_date || n.created_at || "").slice(0, 10)}
                            {n.author_name && <> · {n.author_name}</>}
                          </div>
                          <div className="font-display font-bold text-sm mt-0.5 truncate">{n.title}</div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section data-testid="contact-history-tasks">
                <h3 className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-2">
                  Tasks ({data.counts?.tasks ?? 0})
                </h3>
                {(data.tasks || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No tasks reference this contact.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.tasks.map((t) => (
                      <li key={t.id} className="border border-border/60 bg-card p-3" data-testid={`contact-history-task-${t.id}`}>
                        <Link
                          to={t.engagement_id ? `/engagements/${t.engagement_id}?tab=tasks&openTask=${t.id}` : `/tasks?open=${t.id}`}
                          className="block hover:text-primary"
                        >
                          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                            {t.status} · {t.priority || "medium"}
                            {t.due_date && <> · due {t.due_date}</>}
                          </div>
                          <div className="font-display font-bold text-sm mt-0.5 truncate">{t.title}</div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Iter 85.7 · Introduction Path — surface warm-intro routes. */}
              {intros && (intros.candidates || []).length > 0 && (
                <section data-testid="contact-history-introductions">
                  <h3 className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-2 flex items-center gap-1">
                    <UsersThree size={11} weight="fill" /> Warm intros ({intros.candidates.length})
                  </h3>
                  <p className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                    Recently-touched contacts who could vouch for a follow-up.
                  </p>
                  <ul className="space-y-2">
                    {intros.candidates.map((cand) => (
                      <li
                        key={`${cand.client_id}-${cand.id}`}
                        className="border border-border/60 bg-card p-3"
                        data-testid={`contact-intro-${cand.id}`}
                      >
                        <Link
                          to={`/clients/${cand.client_id}?tab=stakeholders&openContact=${cand.id}`}
                          className="block hover:text-primary"
                        >
                          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                            {cand.kind === "same_client" ? "Same partner org" : cand.client_name}
                            {cand.days_since != null && <> · touched {cand.days_since}d ago</>}
                            {cand.is_primary && <> · primary</>}
                            {cand.owner_name && <> · owner {cand.owner_name}</>}
                          </div>
                          <div className="font-display font-bold text-sm mt-0.5 truncate">
                            {cand.name}{cand.title ? ` · ${cand.title}` : ""}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
